import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Share2, ArrowLeft, Plus, X, Save, Globe, EyeOff, Copy, Check } from "lucide-react";

export default function LookbookEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [lb, setLb] = useState(null);
  const [allDesigns, setAllDesigns] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) { navigate("/"); return; }
    if (user) {
      Promise.all([
        api.get(`/lookbooks/${id}`),
        api.get("/designs"),
      ]).then(([lbRes, dRes]) => {
        setLb(lbRes.data);
        setTitle(lbRes.data.title || "");
        setDescription(lbRes.data.description || "");
        setSelectedIds(lbRes.data.design_ids || []);
        setAllDesigns(dRes.data);
      }).catch((err) => { console.error(err); toast.error("Failed to load"); });
    }
  }, [id, loading, user, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lookbooks/${id}`, {
        title, description, design_ids: selectedIds,
      });
      setLb(data);
      toast.success("Saved");
    } catch (err) { console.error(err); toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/lookbooks/${id}/share`);
      setLb((prev) => ({ ...prev, share_token: data.share_token, is_public: true }));
      toast.success("Public link ready");
    } catch (err) { console.error(err); toast.error("Failed"); }
  };

  const unshare = async () => {
    try {
      await api.post(`/lookbooks/${id}/unshare`);
      setLb((prev) => ({ ...prev, is_public: false }));
      toast.success("Link disabled");
    } catch { toast.error("Failed"); }
  };

  const toggle = (did) => {
    setSelectedIds((cur) => cur.includes(did) ? cur.filter(x => x !== did) : [...cur, did]);
  };

  const publicUrl = lb?.share_token ? `${window.location.origin}/collection/${lb.share_token}` : "";
  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (!lb) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
    </div>
  );

  const selectedDesigns = selectedIds
    .map((sid) => allDesigns.find((d) => d.id === sid))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 pt-24 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Link to="/lookbooks" className="overline flex items-center gap-2 text-zinc-500 hover:text-lime-600 mb-8" data-testid="lookbook-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All lookbooks
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-8">
            <div className="overline text-zinc-500 mb-2">§ Editing lookbook</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full font-serif text-5xl md:text-7xl leading-[0.95] bg-transparent border-b border-zinc-200 focus:border-zinc-950 focus:outline-none py-2"
              placeholder="Season title"
              data-testid="lookbook-title-input"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="A short editorial note…"
              className="w-full mt-4 bg-transparent border-b border-zinc-200 focus:border-zinc-950 focus:outline-none py-2 text-lg font-serif italic resize-none"
              data-testid="lookbook-desc-input"
            />
          </div>
          <div className="lg:col-span-4 flex flex-col gap-2">
            <button
              onClick={save}
              disabled={saving}
              className={`w-full py-3 overline flex items-center justify-center gap-2 ${saving ? "bg-zinc-300 text-zinc-500" : "bg-zinc-950 text-lime-400 hover:bg-lime-400 hover:text-zinc-950"} transition-colors`}
              data-testid="lookbook-save-btn"
            >{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Save</>}</button>

            {lb.is_public && lb.share_token ? (
              <>
                <div className="border border-zinc-950 bg-white p-2 flex items-center gap-1" data-testid="lookbook-share-live">
                  <Globe className="w-3.5 h-3.5 text-lime-600 shrink-0 ml-1" />
                  <input readOnly value={publicUrl} className="flex-1 bg-transparent px-1 py-1 text-xs font-mono outline-none" />
                  <button onClick={copy} className="px-2 py-1 bg-zinc-950 text-zinc-50 overline hover:bg-lime-400 hover:text-zinc-950 flex items-center gap-1" data-testid="lookbook-copy-btn">
                    {copied ? <><Check className="w-3 h-3" /></> : <><Copy className="w-3 h-3" /></>}
                  </button>
                </div>
                <button onClick={unshare} className="py-2 border border-rose-500 text-rose-600 overline hover:bg-rose-500 hover:text-white flex items-center justify-center gap-2" data-testid="lookbook-unshare-btn">
                  <EyeOff className="w-3.5 h-3.5" /> Disable link
                </button>
              </>
            ) : (
              <button onClick={share} className="w-full py-3 border border-lime-500 text-lime-700 overline hover:bg-lime-400 hover:text-zinc-950 flex items-center justify-center gap-2" data-testid="lookbook-share-btn">
                <Share2 className="w-4 h-4" /> Publish
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-6 border-t border-zinc-200 pt-6">
          <div className="overline text-zinc-500">Looks in this book · {selectedIds.length}</div>
          <button onClick={() => setPickerOpen(true)} className="overline flex items-center gap-2 hover:text-lime-600" data-testid="lookbook-add-designs-btn">
            <Plus className="w-3.5 h-3.5" /> Add looks
          </button>
        </div>

        {selectedDesigns.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center">
            <div className="font-serif italic text-2xl text-zinc-500">Nothing selected yet.</div>
            <p className="text-sm text-zinc-500 mt-2">Add designs from your archive to build the spread.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1" data-testid="lookbook-selected-grid">
            {selectedDesigns.map((d, i) => (
              <div key={d.id} className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/5]"} group`}>
                <img src={fileUrl(d.image_path)} alt={d.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <button
                  onClick={() => toggle(d.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-zinc-950/70 backdrop-blur border border-white/10 text-white flex items-center justify-center hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`lookbook-remove-${d.id}`}
                ><X className="w-4 h-4" /></button>
                <div className="absolute bottom-2 left-2 right-2 overline text-white opacity-0 group-hover:opacity-100 transition-opacity">{d.title}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[100] bg-zinc-950/90 backdrop-blur-md flex items-start justify-center py-10 px-4 overflow-y-auto" data-testid="lookbook-picker-modal">
          <div className="w-full max-w-5xl bg-zinc-50 border border-zinc-200 relative p-8">
            <button onClick={() => setPickerOpen(false)} className="absolute top-4 right-4 w-9 h-9 bg-zinc-950 text-zinc-50 hover:bg-lime-400 hover:text-zinc-950 flex items-center justify-center" data-testid="lookbook-picker-close">
              <X className="w-4 h-4" />
            </button>
            <div className="overline text-zinc-500 mb-2">Add from your archive</div>
            <h3 className="font-serif text-3xl mb-6">Pick the looks</h3>
            {allDesigns.length === 0 ? (
              <div className="text-zinc-500 italic">No designs yet — head to the Studio first.</div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-1 max-h-[60vh] overflow-y-auto">
                {allDesigns.map((d) => {
                  const selected = selectedIds.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      onClick={() => toggle(d.id)}
                      className={`relative aspect-[3/4] border-2 overflow-hidden transition-transform hover:scale-105 ${selected ? "border-lime-400 scale-105" : "border-transparent"}`}
                      data-testid={`lookbook-picker-item-${d.id}`}
                    >
                      <img src={fileUrl(d.image_path)} alt={d.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      {selected && <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-lime-400" />}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => setPickerOpen(false)}
              className="mt-6 w-full py-3 bg-zinc-950 text-zinc-50 overline hover:bg-lime-400 hover:text-zinc-950 transition-colors"
              data-testid="lookbook-picker-done"
            >Done ({selectedIds.length} selected)</button>
          </div>
        </div>
      )}
    </div>
  );
}
